import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, updateDoc, deleteDoc, doc, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";

const COLLECTION = "iletisimMesajlari";

type Durum = "yeni" | "okundu";

interface Mesaj {
  id: string;
  ad: string;
  email: string;
  konu: string;
  mesaj: string;
  tarih: Timestamp | null;
  durum: Durum;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  yeni: { label: "Yeni", color: "bg-blue-100 text-blue-700" },
  okundu: { label: "Okundu", color: "bg-gray-100 text-gray-600" },
};

function formatTarih(t: Timestamp | null): string {
  if (!t || typeof t.toDate !== "function") return "—";
  try { return t.toDate().toLocaleString("tr-TR"); } catch { return "—"; }
}

const IletisimMesajlariPage: React.FC = () => {
  const [rows, setRows] = useState<Mesaj[]>([]);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState(false);
  const [filter, setFilter] = useState<string>("hepsi");
  const [selected, setSelected] = useState<Mesaj | null>(null);

  const load = async () => {
    setLoading(true);
    setHata(false);
    try {
      const snap = await getDocs(query(collection(db, COLLECTION), orderBy("tarih", "desc")));
      setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Mesaj[]);
    } catch (e) {
      console.error("İletişim mesajları okunamadı:", e);
      setHata(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => filter === "hepsi" || r.durum === filter);

  const okunduYap = async (id: string) => {
    try {
      await updateDoc(doc(db, COLLECTION, id), { durum: "okundu" });
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, durum: "okundu" } : r)));
      setSelected((prev) => (prev && prev.id === id ? { ...prev, durum: "okundu" } : prev));
    } catch (e) {
      console.error("Durum güncellenemedi:", e);
      window.alert("Durum güncellenemedi, lütfen tekrar deneyin.");
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Bu mesaj silinsin mi?")) return;
    try {
      await deleteDoc(doc(db, COLLECTION, id));
      setRows((prev) => prev.filter((r) => r.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (e) {
      console.error("Silinemedi:", e);
      window.alert("Silme başarısız, lütfen tekrar deneyin.");
    }
  };

  const yeniSayisi = rows.filter((r) => r.durum === "yeni").length;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">İletişim Mesajları</h2>
          <p className="text-sm text-gray-500 mt-0.5">{yeniSayisi} yeni, {rows.length} toplam mesaj · web "İletişim" formundan</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {["hepsi", "yeni", "okundu"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                ${filter === f ? "bg-[#8B1428] text-white" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`}>
              {f === "hepsi" ? "Hepsi" : STATUS_LABELS[f]?.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-400"><i className="fa-solid fa-circle-notch fa-spin text-3xl" /></div>
      ) : hata ? (
        <div className="bg-red-50 text-red-600 rounded-2xl py-12 text-center text-sm">
          Mesajlar yüklenemedi. <button onClick={load} className="underline font-semibold">Tekrar dene</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 py-20 text-center">
          <i className="fa-solid fa-envelope text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Henüz mesaj yok</p>
          <p className="text-gray-400 text-sm mt-1">Web sitesindeki iletişim formu doldurulduğunda burada görünecek</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Ad</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">E-posta</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Konu</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Tarih</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Durum</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((r) => (
                <tr key={r.id} className={`hover:bg-gray-50 transition-colors ${r.durum === "yeni" ? "bg-blue-50/40" : ""}`}>
                  <td className="px-4 py-3 font-medium text-gray-800">
                    <button onClick={() => setSelected(r)} className="hover:text-[#8B1428] text-left">{r.ad}</button>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs hidden sm:table-cell">{r.email}</td>
                  <td className="px-4 py-3 text-gray-700">{r.konu || "—"}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs hidden md:table-cell">{formatTarih(r.tarih)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_LABELS[r.durum]?.color ?? "bg-gray-100 text-gray-600"}`}>
                      {STATUS_LABELS[r.durum]?.label ?? r.durum}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {r.durum === "yeni" && (
                        <button onClick={() => okunduYap(r.id)} className="text-blue-600 hover:text-blue-800 text-xs font-semibold">Okundu</button>
                      )}
                      <button onClick={() => remove(r.id)} className="text-gray-400 hover:text-red-500 transition-colors"><i className="fa-solid fa-trash text-xs" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">{filtered.length} mesaj gösteriliyor</div>
        </div>
      )}

      {/* Mesaj detay modal */}
      {selected && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setSelected(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 relative">
              <button onClick={() => setSelected(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"><i className="fa-solid fa-xmark text-lg" /></button>
              <h3 className="text-lg font-bold text-gray-800 mb-1">{selected.ad}</h3>
              <p className="text-xs text-gray-400 mb-4">{selected.email} · {formatTarih(selected.tarih)}</p>
              {selected.konu && <div className="text-sm font-semibold text-gray-700 mb-2">Konu: {selected.konu}</div>}
              <p className="text-gray-600 text-sm leading-relaxed bg-gray-50 rounded-lg p-4 whitespace-pre-wrap">{selected.mesaj}</p>
              <div className="flex gap-3 mt-5">
                {selected.durum === "yeni" && (
                  <button onClick={() => okunduYap(selected.id)} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 rounded-lg">Okundu İşaretle</button>
                )}
                <button onClick={() => remove(selected.id)} className="flex-1 border border-red-200 text-red-500 hover:bg-red-50 text-sm font-semibold py-2.5 rounded-lg">Sil</button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default IletisimMesajlariPage;
